import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarCheck, CalendarOff, Inbox, Loader2, LogOut, Mail, Phone, Plus, Ticket, Trash2, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LOGO_URL } from "@/App";
import { AdminCalendar } from "@/components/AdminCalendar";
import { VehicleManager } from "@/components/VehicleManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErr = (d) => {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

const fmtReceived = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState(null);
  const [newSlot, setNewSlot] = useState("");
  const [savingSlots, setSavingSlots] = useState(false);
  const [blockedDays, setBlockedDays] = useState(null);
  const [newDay, setNewDay] = useState("");
  const [savingDays, setSavingDays] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [promoCodes, setPromoCodes] = useState(null);
  const [newPromoCode, setNewPromoCode] = useState("");
  const [newPromoNote, setNewPromoNote] = useState("");
  const [newPromoType, setNewPromoType] = useState("percent");
  const [newPromoValue, setNewPromoValue] = useState("");
  const [savingPromo, setSavingPromo] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setAuth(r.data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth) {
      axios
        .get(`${API}/bookings`, { withCredentials: true })
        .then((r) => setBookings(r.data.bookings))
        .catch(() => setBookings([]));
      axios
        .get(`${API}/settings/time-slots`)
        .then((r) => setSlots(r.data.slots))
        .catch(() => setSlots([]));
      axios
        .get(`${API}/settings/blocked-days`)
        .then((r) => setBlockedDays(r.data.days))
        .catch(() => setBlockedDays([]));
      axios
        .get(`${API}/settings/promo-codes`, { withCredentials: true })
        .then((r) => setPromoCodes(r.data.codes))
        .catch(() => setPromoCodes([]));
    }
  }, [auth]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setAuth(data);
      toast.success("Welcome back");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAuth(false);
    setBookings(null);
  };

  const STATUS_STYLES = {
    new: "border-nitro/40 text-nitro bg-nitro/10",
    confirmed: "border-amber-400/40 text-amber-400 bg-amber-400/10",
    done: "border-emerald-400/40 text-emerald-400 bg-emerald-400/10",
  };

  const setStatus = async (booking_id, status) => {
    try {
      await axios.patch(`${API}/bookings/${booking_id}`, { status }, { withCredentials: true });
      setBookings((bs) => bs.map((b) => (b.booking_id === booking_id ? { ...b, status } : b)));
      toast.success(`Booking marked as ${status}`);
    } catch {
      toast.error("Could not update booking");
    }
  };

  const saveSlots = async (next) => {
    setSavingSlots(true);
    try {
      const { data } = await axios.put(
        `${API}/settings/time-slots`,
        { slots: next },
        { withCredentials: true }
      );
      setSlots(data.slots);
      toast.success("Booking slots updated");
    } catch {
      toast.error("Could not update slots");
    } finally {
      setSavingSlots(false);
    }
  };

  const addSlot = () => {
    const v = newSlot.trim();
    if (!v || (slots || []).includes(v)) return;
    saveSlots([...(slots || []), v]);
    setNewSlot("");
  };

  const removeSlot = (s) => saveSlots((slots || []).filter((x) => x !== s));

  const saveDays = async (next) => {
    setSavingDays(true);
    try {
      const { data } = await axios.put(
        `${API}/settings/blocked-days`,
        { days: next },
        { withCredentials: true }
      );
      setBlockedDays(data.days);
      toast.success("Blocked days updated");
    } catch {
      toast.error("Could not update blocked days");
    } finally {
      setSavingDays(false);
    }
  };

  const addDay = () => {
    if (!newDay || (blockedDays || []).includes(newDay)) return;
    saveDays([...(blockedDays || []), newDay]);
    setNewDay("");
  };

  const removeDay = (d) => saveDays((blockedDays || []).filter((x) => x !== d));

  const toggleDay = (day) => {
    if ((blockedDays || []).includes(day)) {
      removeDay(day);
    } else {
      saveDays([...(blockedDays || []), day]);
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      setBookings((bs) => bs.filter((b) => b.booking_id !== id));
      setConfirmDelete(null);
      toast.success("Booking deleted");
    } catch {
      toast.error("Could not delete booking");
    }
  };

  const savePromos = async (next) => {
    setSavingPromo(true);
    try {
      const { data } = await axios.put(
        `${API}/settings/promo-codes`,
        { codes: next },
        { withCredentials: true }
      );
      setPromoCodes(data.codes);
      toast.success("Promo codes updated");
    } catch {
      toast.error("Could not update promo codes");
    } finally {
      setSavingPromo(false);
    }
  };

  const addPromo = () => {
    const code = newPromoCode.trim().toUpperCase();
    if (!code || (promoCodes || []).some((c) => c.code === code)) return;
    savePromos([
      ...(promoCodes || []),
      {
        code,
        note: newPromoNote.trim(),
        discount_type: newPromoType,
        discount_value: parseFloat(newPromoValue) || 0,
      },
    ]);
    setNewPromoCode("");
    setNewPromoNote("");
    setNewPromoValue("");
  };

  const removePromo = (code) => savePromos((promoCodes || []).filter((c) => c.code !== code));

  return (
    <div className="min-h-screen bg-asphalt text-neutral-100 font-body carbon-grid">
      <header className="border-b border-[#1F232D] bg-asphalt/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-5 h-[70px] flex items-center justify-between">
          <a href="/" data-testid="admin-home-link" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="bm solutions logo"
              className="h-10 w-10 object-cover rounded-full ring-1 ring-[#262933]"
            />
            <span className="font-heading font-bold text-xl uppercase">
              bm <span className="text-nitro">solutions</span>
            </span>
          </a>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500">
            Owner Diary
          </span>
        </div>
      </header>

      {auth === null && (
        <div className="flex justify-center py-32">
          <Loader2 className="animate-spin text-nitro" size={32} />
        </div>
      )}

      {auth === false && (
        <div className="flex items-center justify-center px-5 py-16 sm:py-24">
          <motion.form
            data-testid="admin-login-form"
            onSubmit={login}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="glass-panel rounded-2xl p-8 sm:p-10 w-full max-w-md"
          >
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-nitro mb-2">
              Private Access
            </p>
            <h1 className="font-heading font-bold uppercase text-3xl text-neutral-100 mb-8">
              Bookings Diary
            </h1>
            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="adm-email" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                  Email
                </Label>
                <Input
                  id="adm-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-11 mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="adm-pass" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                  Password
                </Label>
                <Input
                  id="adm-pass"
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-11 mt-1.5"
                />
              </div>
              {error && (
                <p data-testid="admin-login-error" className="text-sm text-nitro font-mono">
                  {error}
                </p>
              )}
              <Button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="mt-2 bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-widest rounded-full py-6 transition-all duration-300 hover:shadow-[0_0_25px_rgba(255,46,77,0.45)] disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
              </Button>
            </div>
          </motion.form>
        </div>
      )}

      {auth && (
        <main data-testid="bookings-diary" className="max-w-6xl mx-auto px-5 py-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-nitro mb-2">
                Signed in as {auth.email}
              </p>
              <h1 className="font-heading font-bold uppercase text-4xl text-neutral-100">
                Bookings Diary
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-400 border border-[#262933] rounded-full px-4 py-2 bg-panel">
                {bookings ? bookings.length : 0} booking{bookings && bookings.length === 1 ? "" : "s"}
              </span>
              <Button
                data-testid="admin-logout-button"
                onClick={logout}
                variant="outline"
                className="border-[#262933] bg-transparent hover:bg-panel hover:border-nitro text-neutral-200 font-heading uppercase tracking-wider rounded-full"
              >
                <LogOut size={15} className="mr-2" /> Sign Out
              </Button>
            </div>
          </div>

          <div data-testid="slot-manager" className="glass-panel rounded-xl p-6 mb-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-nitro mb-1">
                  Booking Form
                </p>
                <h2 className="font-heading font-bold uppercase text-2xl text-neutral-100">
                  Available Time Slots
                </h2>
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="slot-add-input"
                  value={newSlot}
                  onChange={(e) => setNewSlot(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSlot();
                    }
                  }}
                  placeholder="e.g. 17:30"
                  className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-10 w-32"
                />
                <Button
                  data-testid="slot-add-button"
                  onClick={addSlot}
                  disabled={savingSlots || !newSlot.trim()}
                  className="bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-wider rounded-full px-5 disabled:opacity-60"
                >
                  {savingSlots ? (
                    <Loader2 className="animate-spin" size={15} />
                  ) : (
                    <>
                      <Plus size={15} className="mr-1" /> Add
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {slots === null && <Loader2 className="animate-spin text-nitro" size={18} />}
              {slots?.map((s) => (
                <span
                  key={s}
                  data-testid="slot-chip"
                  className="flex items-center gap-2 font-mono text-xs tracking-widest text-neutral-200 border border-[#262933] rounded-full px-4 py-2 bg-[#0E1015]"
                >
                  {s}
                  <button
                    data-testid={`slot-remove-${s}`}
                    onClick={() => removeSlot(s)}
                    aria-label={`Remove slot ${s}`}
                    className="text-neutral-500 hover:text-nitro transition-colors"
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
              {slots && slots.length === 0 && (
                <p className="text-sm text-neutral-500">
                  No slots — customers won't be able to pick a time.
                </p>
              )}
            </div>
            <div className="mt-6 pt-6 border-t border-[#1F232D]">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h3 className="font-heading font-semibold uppercase text-lg text-neutral-100 flex items-center gap-2">
                  <CalendarOff size={17} className="text-nitro" /> Blocked Days Off
                </h3>
                <div className="flex gap-2">
                  <Input
                    data-testid="blocked-day-input"
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={newDay}
                    onChange={(e) => setNewDay(e.target.value)}
                    className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-10 w-40"
                  />
                  <Button
                    data-testid="blocked-day-add-button"
                    onClick={addDay}
                    disabled={savingDays || !newDay}
                    className="bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-wider rounded-full px-5 disabled:opacity-60"
                  >
                    {savingDays ? (
                      <Loader2 className="animate-spin" size={15} />
                    ) : (
                      <>
                        <Plus size={15} className="mr-1" /> Block
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {blockedDays === null && <Loader2 className="animate-spin text-nitro" size={18} />}
                {blockedDays?.map((d) => (
                  <span
                    key={d}
                    data-testid="blocked-day-chip"
                    className="flex items-center gap-2 font-mono text-xs tracking-widest text-neutral-200 border border-[#262933] rounded-full px-4 py-2 bg-[#0E1015]"
                  >
                    {d}
                    <button
                      data-testid={`blocked-day-remove-${d}`}
                      onClick={() => removeDay(d)}
                      aria-label={`Unblock ${d}`}
                      className="text-neutral-500 hover:text-nitro transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
                {blockedDays && blockedDays.length === 0 && (
                  <p className="text-sm text-neutral-500">No blocked days — every date is bookable.</p>
                )}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#1F232D]">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h3 className="font-heading font-semibold uppercase text-lg text-neutral-100 flex items-center gap-2">
                  <Ticket size={17} className="text-nitro" /> Promo Codes
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Input
                    data-testid="promo-code-new-input"
                    value={newPromoCode}
                    onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                    placeholder="CODE"
                    className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-10 w-28 uppercase"
                  />
                  <Input
                    data-testid="promo-note-new-input"
                    value={newPromoNote}
                    onChange={(e) => setNewPromoNote(e.target.value)}
                    placeholder="e.g. 10% off Stage 1"
                    className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-10 w-44"
                  />
                  <Select value={newPromoType} onValueChange={setNewPromoType}>
                    <SelectTrigger
                      data-testid="promo-type-select"
                      className="bg-[#0E1015] border-[#262933] text-neutral-100 h-10 w-28"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-panel border-[#262933] text-neutral-100">
                      <SelectItem value="percent" className="focus:bg-nitro/20 focus:text-white">% off</SelectItem>
                      <SelectItem value="fixed" className="focus:bg-nitro/20 focus:text-white">£ off</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid="promo-value-input"
                    type="number"
                    min="0"
                    value={newPromoValue}
                    onChange={(e) => setNewPromoValue(e.target.value)}
                    placeholder="50"
                    className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-10 w-20"
                  />
                  <Button
                    data-testid="promo-add-button"
                    onClick={addPromo}
                    disabled={savingPromo || !newPromoCode.trim()}
                    className="bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-wider rounded-full px-5 disabled:opacity-60"
                  >
                    {savingPromo ? (
                      <Loader2 className="animate-spin" size={15} />
                    ) : (
                      <>
                        <Plus size={15} className="mr-1" /> Add
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {promoCodes === null && <Loader2 className="animate-spin text-nitro" size={18} />}
                {promoCodes?.map((c) => (
                  <span
                    key={c.code}
                    data-testid="promo-chip"
                    className="flex items-center gap-2 font-mono text-xs tracking-widest text-neutral-200 border border-[#262933] rounded-full px-4 py-2 bg-[#0E1015]"
                  >
                    {c.code}
                    {c.discount_value > 0 && (
                      <span className="text-emerald-400 tracking-normal">
                        · {c.discount_type === "percent" ? `${c.discount_value}% off` : `£${c.discount_value} off`}
                      </span>
                    )}
                    {c.note && <span className="text-neutral-500 tracking-normal">· {c.note}</span>}
                    <button
                      data-testid={`promo-remove-${c.code}`}
                      onClick={() => removePromo(c.code)}
                      aria-label={`Remove promo ${c.code}`}
                      className="text-neutral-500 hover:text-nitro transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
                {promoCodes && promoCodes.length === 0 && (
                  <p className="text-sm text-neutral-500">No promo codes yet — add one to offer discounts.</p>
                )}
              </div>
            </div>

            <p className="mt-4 font-mono text-[9px] tracking-[0.2em] uppercase text-neutral-600">
              Changes apply to the booking form instantly — blocked dates can't be picked
            </p>
          </div>

          <VehicleManager />

          <div data-testid="diary-calendar" className="glass-panel rounded-xl p-6 mb-10">
            <AdminCalendar
              bookings={bookings || []}
              blockedDays={blockedDays || []}
              onToggleDay={toggleDay}
            />
          </div>

          {bookings === null && (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-nitro" size={28} />
            </div>
          )}

          {bookings && bookings.length === 0 && (
            <div className="glass-panel rounded-2xl p-16 flex flex-col items-center text-center">
              <Inbox size={40} className="text-neutral-600 mb-4" />
              <p className="font-heading font-semibold uppercase text-xl text-neutral-300">
                No bookings yet
              </p>
              <p className="text-sm text-neutral-500 mt-2">
                New booking requests from the website will appear here.
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {bookings?.map((b, i) => (
              <motion.article
                key={b.booking_id}
                data-testid="booking-card"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="glass-panel rounded-xl p-5 hover:border-nitro/50 transition-colors duration-300"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <span className="font-heading font-bold text-xl text-nitro flex items-center gap-2">
                    <CalendarCheck size={17} />
                    {b.preferred_date} · {b.time_slot}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-[9px] tracking-[0.25em] uppercase px-2.5 py-1 rounded-full border ${STATUS_STYLES[b.status] || STATUS_STYLES.new}`}>
                      {b.status}
                    </span>
                    <button
                      data-testid="booking-delete-button"
                      onClick={() =>
                        confirmDelete === b.booking_id
                          ? deleteBooking(b.booking_id)
                          : setConfirmDelete(b.booking_id)
                      }
                      onBlur={() => setConfirmDelete(null)}
                      aria-label="Delete booking"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors duration-300 ${
                        confirmDelete === b.booking_id
                          ? "border-nitro bg-nitro text-white"
                          : "border-[#262933] text-neutral-500 hover:text-nitro hover:border-nitro/50"
                      }`}
                    >
                      <Trash2 size={12} />
                      {confirmDelete === b.booking_id && (
                        <span className="font-mono text-[9px] tracking-[0.2em] uppercase">Sure?</span>
                      )}
                    </button>
                  </div>
                </div>
                <p className="font-heading font-semibold uppercase text-lg text-neutral-100 flex items-center gap-2">
                  <User size={15} className="text-neutral-500" />
                  {b.full_name}
                </p>
                <p className="text-sm text-neutral-300 mt-1 leading-relaxed">{b.vehicle}</p>
                {b.registration && (
                  <p
                    data-testid="booking-registration"
                    className="mt-1 font-mono text-xs tracking-[0.15em] text-neutral-100 bg-[#0E1015] border border-[#262933] rounded px-2 py-1 inline-block"
                  >
                    {b.registration}
                  </p>
                )}
                {b.promo_code && (
                  <p
                    data-testid="booking-promo"
                    className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-amber-400 flex items-center gap-1.5"
                  >
                    <Ticket size={11} />
                    {b.promo_code}{b.promo_note ? ` — ${b.promo_note}` : ""}
                  </p>
                )}
                {b.quote_total != null && (
                  <p
                    data-testid="booking-quote"
                    className="mt-2 font-mono text-xs text-emerald-400 flex items-center gap-2"
                  >
                    Quote: £{b.quote_total}
                    {b.quote_base != null && b.quote_total < b.quote_base && (
                      <span className="line-through text-neutral-500">£{b.quote_base}</span>
                    )}
                  </p>
                )}
                <div className="mt-3 flex flex-col gap-1.5 text-xs font-mono text-neutral-400">
                  <span className="flex items-center gap-2">
                    <Phone size={12} className="text-nitro" />
                    {b.phone}
                  </span>
                  <span className="flex items-center gap-2 break-all">
                    <Mail size={12} className="text-nitro shrink-0" />
                    {b.email}
                  </span>
                </div>
                {b.message && (
                  <p className="mt-3 text-xs text-neutral-400 border-t border-[#1F232D] pt-3 leading-relaxed">
                    “{b.message}”
                  </p>
                )}
                <p className="mt-3 font-mono text-[9px] tracking-[0.2em] uppercase text-neutral-600">
                  Received {fmtReceived(b.created_at)}
                </p>
                {b.status === "new" && (
                  <Button
                    data-testid="booking-confirm-button"
                    onClick={() => setStatus(b.booking_id, "confirmed")}
                    className="mt-4 w-full bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-widest rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,46,77,0.4)]"
                  >
                    Mark Confirmed
                  </Button>
                )}
                {b.status === "confirmed" && (
                  <Button
                    data-testid="booking-done-button"
                    onClick={() => setStatus(b.booking_id, "done")}
                    variant="outline"
                    className="mt-4 w-full border-emerald-400/40 bg-transparent hover:bg-emerald-400/10 text-emerald-400 font-heading font-semibold uppercase tracking-widest rounded-full transition-colors duration-300"
                  >
                    Mark Done
                  </Button>
                )}
              </motion.article>
            ))}
          </div>
        </main>
      )}
    </div>
  );
}
